import React, { createRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CustomFilter } from 'app/ComponentLibrary/Filter/CustomFilter';
import { FilterCompTypes } from 'app/ComponentLibrary/Filter/utils';
import { TaskCategory, TaskEntity, TaskFilterType } from 'app/models/Tasks/Tasks';
import { getStatusList } from 'app/services/TaskServices/TaskServices';
import { getFormattedTaskGridFilters } from 'app/components/PerformanceReport/Task/Filters/utils';
import { getTaskCategories } from 'app/services/SharedServices/SharedServices';
import {
  convertArrayToKeyValuePairs,
  convertSelectedFilterValue,
  formatDateValue,
  formatSelectedFilter,
  returnTaskFilterBody,
  setFormValues,
  TaskSearchFilterName,
} from './TaskFilterUtilitis';
import { gridStateActions } from 'app/components/Common/Grid/GridStateManagement/GridState.redux';
import { selectUser } from 'oidc/user.selectors';
import { selectGridState } from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { useFormContext } from 'react-hook-form';
import { RootState } from 'types';
import { selectTaskEntity } from '@AMIEWEB/Tasks/store/Tasks.selectors';
import { defaultTaskFilters } from './ITaskFilter';
import { Collapse, Grid, IconButton } from '@mui/material';
import { FunnelButton } from 'app/ComponentLibrary/Filter/SearchPageIcons/FunnelButton';
import FunnelOutlineWithBlue from 'app/assets/images/IconImages/FunnelOutlineWithBlue.svg';
import { makeStyles } from 'tss-react/mui';
import { ResetButton } from 'app/ComponentLibrary/Filter/SearchPageIcons/ResetButton';

const useStyles = makeStyles()({
  funnelBtn: {
    padding: 0,
    paddingLeft: '4px',
    height: '34px',
    width: '34px',
  },
  FilterIcon: {
    padding: 0,
    margin: '-7px -7px -12px -8px',
  },
  resetButtonVersion2: {
    padding: 0,
    height: '34px',
    width: '34px',
  },
  actionContainer: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
  },
});
export const TaskSearchFilters = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { defaultValues, isLinkTaskMod, setFiltersExpanded } = props;
  const { setValue, getValues } = useFormContext();
  const hiddenSubmitButton = createRef<HTMLButtonElement>();
  const { filterAttributes } = useSelector(selectGridState);
  const taskEntity = useSelector(selectTaskEntity);
  const filterPreferences = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === 'GlobalSearchGrid-tasks');
  });
  const { userInfo } = useSelector(selectUser);
  const divRef = useRef<HTMLDivElement>(null);
  const { classes } = useStyles();

  const [checked, setChecked] = React.useState(isLinkTaskMod ? false : true);
  const [hoverFunnel, setHoverFunnel] = useState(false);
  const [hoverReset, setHoverReset] = useState(false);

  const handleChange = () => {
    setChecked(prev => !prev);
    setFiltersExpanded(prev => !prev);
  };
  const applyDefaultValues = () => {
    Object.entries(defaultValues).map(item => setValue(item[0], item[1]));
    setFormValues(defaultValues, setValue);
    const selectedFilterChips = convertSelectedFilterValue(Object.entries(defaultValues));

    dispatch(
      gridStateActions.setFilterAttributes({
        filterObject: Object.entries(defaultValues),
        selectedChipFilters: isLinkTaskMod
          ? selectedFilterChips
          : { ...defaultTaskFilters, assignedToIds: [userInfo?.employeeId] },
      }),
    );
  };

  const resetFilter = () => {
    if (filterPreferences?.value && taskEntity === TaskEntity.ALL && !isLinkTaskMod) {
      const selectedFilterName = filterPreferences?.value?.selectedFilter?.name;
      const filterSet = filterPreferences?.value?.filterAttributes?.filterSet?.find(
        item => item.name === selectedFilterName,
      );
      if (filterSet) {
        const selectedFilter = {
          name: filterSet?.name,
          value: filterSet?.value,
          isDefault: filterSet?.isDefault,
        };
        const selectedFilterValue = formatSelectedFilter(selectedFilter?.value);
        const selectedFilters = convertArrayToKeyValuePairs(selectedFilterValue);
        let DefaultDateValue: any = null;
        if (selectedFilters?.dueDate) {
          DefaultDateValue = formatDateValue(selectedFilters);
        }
        setFormValues(selectedFilters, setValue, DefaultDateValue);
        const selectedFilterChips = convertSelectedFilterValue(selectedFilter?.value);
        dispatch(
          gridStateActions?.setFilterAttributes({
            filterObject: selectedFilterValue,
            selectedChipFilters: selectedFilterChips,
          }),
        );
      } else {
        applyDefaultValues();
      }
    } else {
      applyDefaultValues();
    }
  };

  const onFilterChangeValues = (fieldName, newValue) => {
    if (['dueDate'].includes(fieldName)) {
      const newFilters = returnTaskFilterBody(fieldName, newValue, filterAttributes?.selectedChipFilters);
      dispatch(gridStateActions.setSelectedChips(newFilters));
      hiddenSubmitButton.current?.click();
    }
  };

  const getFormatedStatusList = async keyword => {
    const list = await getStatusList();
    var formatedList: any[] = [];
    list.map(data => {
      if (!keyword || data?.name?.toLowerCase().includes(keyword.toLowerCase())) {
        const format = { id: data.id, name: data.name, value: data.id };
        formatedList.push(format);
      }
      return [];
    });
    return formatedList;
  };

  const getTaskFilterSpecs = () => [
    {
      filterName: t('search.globalSearch.task.filterNames.assignedTo'),
      name: TaskSearchFilterName.assignedTo,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: keyword => getFormattedTaskGridFilters(TaskFilterType.ASSIGNEDTO, keyword),
    },
    {
      filterName: t('search.globalSearch.task.filterNames.taskCategory'),
      name: TaskSearchFilterName.taskCategory,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: async (keyword: string) => {
        const data = await getTaskCategories(true);
        const list = Array.isArray(data) ? data : [];
        const filtered =
          !keyword || !keyword.trim()
            ? list
            : list.filter((d: any) =>
                ((d.name ?? d.Name) || '').toLowerCase().includes(keyword.toLowerCase().trim()),
              );
        return filtered.map((d: any) => {
          const value = d.value ?? d.Value;
          const name = d.name ?? d.Name ?? String(value);
          return {
            id: value,
            name,
            value,
          };
        });
      },
    },
    {
      filterName: t('search.globalSearch.task.filterNames.taskName'),
      name: TaskSearchFilterName.taskName,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      selectAllValue: 'All',
      fetchOptions: keyword => getFormattedTaskGridFilters(TaskFilterType.TASKNAME, keyword),

      hideFilter: true,
    },
    {
      filterName: t('search.globalSearch.task.filterNames.status'),
      name: TaskSearchFilterName.status,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: keyword => getFormatedStatusList(keyword),
      hasControlError: !filterAttributes?.selectedChipFilters?.taskStatusIds?.length,
    },
    {
      filterName: t('search.globalSearch.task.filterNames.dueDate'),
      name: TaskSearchFilterName.dueDate,
      type: FilterCompTypes.DATERANGEV2,
      variant: 'outlined',
      placeholder: t('search.globalSearch.task.filterNames.dueDate'),
      maxWidth: 210,
      version2: true,
      single: false,
      quickSelect: false,
    },
    {
      filterName: t('search.globalSearch.task.filterNames.candidateName'),
      name: TaskSearchFilterName.candidateName,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: keyword => getFormattedTaskGridFilters(TaskFilterType.CANDIDATE, keyword),
    },
    {
      filterName: t('search.globalSearch.task.filterNames.candidateId'),
      name: TaskSearchFilterName.candidateId,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: keyword => getFormattedTaskGridFilters(TaskFilterType.CANDIDATEID, keyword),
    },
    {
      filterName: t('search.globalSearch.task.filterNames.placementId'),
      name: TaskSearchFilterName.placementId,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: keyword => getFormattedTaskGridFilters(TaskFilterType.PLACEMENTID, keyword),
    },
    {
      filterName: t('search.globalSearch.task.filterNames.orderId'),
      name: TaskSearchFilterName.orderId,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: keyword => getFormattedTaskGridFilters(TaskFilterType.ORDERID, keyword),
      searchOnCharacterCount: 1,
      applyOnClickAway: true,
    },
    {
      filterName: t('search.globalSearch.task.filterNames.facility'),
      name: TaskSearchFilterName.facility,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: keyword => getFormattedTaskGridFilters(TaskFilterType.FACILITY, keyword),
    },
    {
      filterName: t('search.globalSearch.task.filterNames.facilityId'),
      name: TaskSearchFilterName.facilityId,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: keyword => getFormattedTaskGridFilters(TaskFilterType.FACILITYID, keyword),
    },
    {
      filterName: t('search.globalSearch.task.filterNames.createdBy'),
      name: TaskSearchFilterName.createdBy,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: keyword => getFormattedTaskGridFilters(TaskFilterType.CREATEDBY, keyword),
    },
  ];
  const [fields, setFields] = useState<any>(getTaskFilterSpecs());

  useEffect(() => {
    if (!filterAttributes?.selectedChipFilters?.taskCategories) return;
    const hasRFOFilter = filterAttributes?.selectedChipFilters.taskCategories.find(
      f => f === TaskCategory.MISSINGRFOREQS,
    );
    // checks if RFO filter is in category list
    // else refresh list to update filters applied
    if (hasRFOFilter) {
      if (!fields[2]?.hideFilter) return;
      const newFilterSpecs = getTaskFilterSpecs();
      newFilterSpecs[2].hideFilter = false;
      setFields(newFilterSpecs);
    } else {
      setFields(getTaskFilterSpecs());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAttributes?.selectedChipFilters]);
  return (
    <Grid container>
      <Grid item xs={11}>
        <Collapse in={checked} collapsedSize={38}>
          <div ref={divRef}>
            <CustomFilter
              fields={fields}
              justifyContent={'flex-start'}
              version2
              isLinkTaskMod={isLinkTaskMod}
              onResetForm={() => resetFilter()}
              onChangeValues={onFilterChangeValues}
              onApply={(fieldName, newValue) => {
                const newFilters = returnTaskFilterBody(fieldName, newValue, filterAttributes?.selectedChipFilters);
                // remove task names if rfo is not in categories
                if (
                  fieldName === TaskSearchFilterName.taskCategory &&
                  newFilters.taskCategories?.length > 0 &&
                  !newFilters.taskCategories?.find(f => f === TaskCategory.MISSINGRFOREQS) &&
                  newFilters.taskName?.length > 0
                ) {
                  newFilters.taskName = [];
                  setValue(TaskSearchFilterName.taskName, []);
                }
                dispatch(gridStateActions.setSelectedChips(newFilters));
                props.onApplyFilter(getValues());
              }}
            />
          </div>
        </Collapse>
      </Grid>
      {isLinkTaskMod && (
        <Grid item xs={1} className={classes.actionContainer}>
          <IconButton
            color="primary"
            type="button"
            disableTouchRipple
            disableFocusRipple
            onMouseEnter={() => setHoverReset(true)}
            onMouseLeave={() => setHoverReset(false)}
            classes={{ root: classes.resetButtonVersion2 }}
            onClick={() => resetFilter()}
            size="small"
          >
            <ResetButton toggleReset={hoverReset} />
          </IconButton>
          {(divRef?.current?.clientHeight ?? 0) > 38 && (
            <IconButton
              color="primary"
              type="button"
              disableTouchRipple
              disableFocusRipple
              onMouseEnter={() => setHoverFunnel(true)}
              onMouseLeave={() => setHoverFunnel(false)}
              onClick={() => handleChange()}
              size="small"
              classes={{ root: classes.funnelBtn }}
            >
              {checked ? (
                <img src={FunnelOutlineWithBlue} className={classes.FilterIcon} alt="Funnel" />
              ) : (
                <FunnelButton toggle={hoverFunnel} />
              )}
            </IconButton>
          )}
        </Grid>
      )}
    </Grid>
  );
};
